<template>
  <div>
    <div
      class="layout-sticky w-full min-h-screen"
      :class="{
        'scrolled': isScrolled,
        'no-navbar': !$slots.navbar,
      }"
    >
      <div v-if="$slots.background" class="background">
        <slot name="background" />
      </div>

      <TheShamrockBackground v-if="shamrock" class="the-shamrock" shamrock-class="shamrock" />

      <div class="header-container flex w-full" data-containerid="page_header">
        <div class="header w-full mx-auto">
          <slot name="header" />
        </div>
      </div>

      <main :class="['content-wrap px-100', mainClass]" data-containerid="page_content">
        <ActiveAnnouncement class="pb-200" />
        <slot />
      </main>

      <LazyTheNavbar v-if="$slots.navbar">
        <slot name="navbar" />
      </LazyTheNavbar>
    </div>

    <div v-if="$slots.after" data-containerid="page_footer">
      <slot name="after" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useEventListener } from '@vueuse/core'

type StickyLayoutProps = {
  headerHeight?: string
  mainClass?: string
  shamrock?: boolean
}

const IS_SCROLLED_AFTER_PX = 20

const { headerHeight = '5rem', shamrock = false } = defineProps<StickyLayoutProps>()

const isScrolled = ref(false)

const handleIsScrolled = () => {
  const scrollY = window.scrollY
  const numberOfTeleports = document.getElementById('teleports')?.childNodes.length ?? 0

  if (scrollY <= 0 && numberOfTeleports > 0) return

  isScrolled.value = window.scrollY > IS_SCROLLED_AFTER_PX
}

useEventListener(window, 'scroll', handleIsScrolled)

onMounted(handleIsScrolled)
</script>

<style lang="scss" scoped>
.layout-sticky {
  position: relative;
  display: grid;
  grid-template-rows: v-bind(headerHeight) 1fr $main-bottom-navigation-height;
  grid-template-columns: minmax(0, 1fr);

  .background {
    position: absolute;
    inset: 0;
    z-index: -1;
  }

  .header-container {
    @include transition-default(background-color);

    position: fixed;
    top: 0;
    z-index: 120;
    min-height: v-bind(headerHeight);

    .header {
      max-width: $main-content-width;
    }
  }

  .content-wrap {
    grid-row: 2;
  }

  &.no-navbar {
    grid-template-rows: v-bind(headerHeight) 1fr;

    .content-wrap {
      padding-bottom: $main-bottom-navigation-height;
    }
  }

  &.scrolled {
    .header-container {
      background-color: token('color/background/primary/primary');
      box-shadow: token('shadow/shadow/card/large');
    }
  }
}

.the-shamrock {
  margin-top: 8.75rem;

  :deep(.shamrock) {
    opacity: 0.15;
  }
}
</style>
